import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import api from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Bell, TrendingUp, TrendingDown, CheckCheck, PauseCircle, PlayCircle } from "lucide-react";

export default function Notifications() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const seen = useRef(new Set());
  const first = useRef(true);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      // toast on newly arrived notifications (skip very first load)
      if (!first.current) {
        data.notifications.filter((n) => !seen.current.has(n.id)).slice(0, 3).forEach((n) => {
          if (n.type === "pause") toast.warning(n.message);
          else if (n.type === "resume") toast.info(n.message);
          else toast[n.type === "close" ? (n.pnl >= 0 ? "success" : "error") : "info"](n.message);
        });
      }
      data.notifications.forEach((n) => seen.current.add(n.id));
      first.current = false;
      setItems(data.notifications);
      setUnread(data.unread);
    } catch (_) {}
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, []);

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); setUnread(0); load(); } catch (_) {}
  };

  return (
    <Popover onOpenChange={(o) => { if (o && unread) markAll(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell"
          className="relative p-2 text-[#9CA3AF] hover:text-white transition-colors duration-200 border border-[#1E2028] hover:border-[#4B5563]">
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span data-testid="notif-badge"
              className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 bg-[#FACC15] text-[#05050A] text-[9px] font-bold rounded-full grid place-items-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-[#0F1015] border-[#1E2028] text-white p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-[#1E2028]">
          <span className="text-sm font-display font-bold">{t("notifications")}</span>
          <button data-testid="notif-mark-all" onClick={markAll} className="flex items-center gap-1 text-[10px] font-mono text-[#9CA3AF] hover:text-white">
            <CheckCheck className="w-3 h-3" /> {t("markAllRead")}
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <div className="py-10 text-center text-xs text-[#6B7280] font-mono">{t("noNotifications")}</div>
          ) : items.map((n) => (
            <div key={n.id} data-testid="notif-item"
              className={`flex items-start gap-2.5 px-3 py-2.5 border-b border-[#1E2028] ${n.read ? "opacity-60" : ""}`}>
              <span className={`mt-0.5 shrink-0 ${n.type === "close" ? (n.pnl >= 0 ? "text-[#10B981]" : "text-[#EF4444]") : n.type === "pause" ? "text-[#EF4444]" : n.type === "resume" ? "text-[#10B981]" : "text-[#8B5CF6]"}`}>
                {n.type === "pause" ? <PauseCircle className="w-4 h-4" /> : n.type === "resume" ? <PlayCircle className="w-4 h-4" /> : n.direction === "BUY" ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              </span>
              <div className="min-w-0">
                <div className="text-xs text-[#D1D5DB] leading-snug">{n.message}</div>
                <div className="text-[10px] text-[#6B7280] font-mono mt-0.5">{new Date(n.created_at).toLocaleTimeString()}</div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
